import { cn, NumberInput, NumberInputProps } from '@heroui/react'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import { motion } from 'framer-motion'

export interface NumberInputFormikProps extends Omit<NumberInputProps, 'classNames'> {
  label?: string
  classNameContainer?: string
  variant?: 'bordered' | 'flat' | 'faded' | 'underlined'
  classNames?: {
    label?: string[]
    input?: string[]
    innerWrapper?: string
    inputWrapper?: string[]
  }
  labelPlacement?: 'outside' | 'inside' | 'outside-left'
  name: string
  isRequired?: boolean
}

const NumberFormik = ({
  label,
  classNameContainer,
  variant = 'bordered',
  classNames,
  labelPlacement = 'outside',
  name,
  isRequired = false,
  ...props
}: NumberInputFormikProps) => {
  return (
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => {
        const handleValueChange = (value: number | undefined) => {
          form.setFieldValue(name, value)
          form.setFieldTouched(name, true, false)
        }

        return (
          <motion.div className={cn(classNameContainer)}>
            <NumberInput
              fullWidth
              className="h-full"
              errorMessage={meta.error || meta.touched ? meta.error : ''}
              isRequired={isRequired}
              label={label}
              labelPlacement={labelPlacement}
              radius="sm"
              value={typeof field.value === 'number' ? field.value : undefined}
              variant={variant}
              classNames={{
                label: [
                  'text-gray-900',
                  'dark:text-foreground',
                  'group-data-[filled-within=true]:text-gray-900',
                  'dark:group-data-[filled-within=true]:text-white',
                  'group-data-[invalid=true]:text-danger',
                  'dark:group-data-[invalid=true]:text-danger',
                ],
                input: [
                  'bg-transparent',
                  'text-gray-900',
                  'dark:text-white',
                  'placeholder:text-gray-500',
                  'dark:placeholder:text-white/60',
                  'group-data-[invalid=true]:placeholder:text-danger',
                ],
                innerWrapper: 'bg-transparent',
                inputWrapper: [
                  'bg-gray-50',
                  'dark:bg-[#1e1e20]',
                  'border-gray-200',
                  'dark:border-default-600',
                  'hover:bg-gray-100',
                  'dark:hover:bg-[#1e1e20]/70',
                  'group-data-[focus=true]:bg-gray-100',
                  'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                  'group-data-[focus=true]:border-primary',
                  'dark:group-data-[focus=true]:border-primary',
                  '!cursor-text',
                ],
                stepperButton: [
                  '!h-4',
                  '!w-6',
                  'rounded-full',
                  'hover:bg-gray-100',
                  'dark:hover:bg-gray-700',
                ],
                ...classNames,
              }}
              onBlur={field.onBlur}
              onValueChange={handleValueChange}
              {...props}
            />
          </motion.div>
        )
      }}
    </Field>
  )
}

export default NumberFormik
